import React, { HTMLProps } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@design-system';
import { InputField } from '@design-system/inputs';
import { yupResolver } from '@hookform/resolvers/yup';
import { AuthActionLink } from '@pages/AuthPages/components/AuthActionLink';
import { AuthSocialLogin } from '@pages/AuthPages/components/AuthSocialLogin';
import { isCincelHost } from 'utils';
import {
  DEFAULT_VALUES,
  ISignupFormValuesProps,
  SignupFormValidationSchema,
} from './helpers';
import { Form } from './components/Form';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';

export interface ISignupFormProps
  extends Omit<HTMLProps<HTMLElement>, 'onSubmit'> {
  /**
   * Custom CSS tailwind styles.
   */
  className?: string;
  /**
   * Form default values.
   */
  defaultValues?: ISignupFormValuesProps;
  /**
   * On submit action handler.
   */
  onSubmit: (v: ISignupFormValuesProps) => void;
  /**
   * White label's primary color.
   */
  primaryColor: string;
  /**
   * Auth0 login handler.
   */
  onAuth0Login?: (conn: 'google-oauth2' | 'windowslive') => void;
  /**
   * Auth0 loading state.
   */
  isAuth0Loading?: boolean;
}

export const SignupForm: React.FC<ISignupFormProps> = (props): JSX.Element => {
  const {
    className,
    defaultValues = DEFAULT_VALUES,
    onSubmit,
    primaryColor,
    onAuth0Login,
    isAuth0Loading,
    ...rest
  } = props;

  const { control, formState, handleSubmit, watch } = useForm<ISignupFormValuesProps>({
    defaultValues: DEFAULT_VALUES,
    mode: 'all',
    resolver: yupResolver(SignupFormValidationSchema),
  });

  const { t } = useTranslation();
  const currentEmail = watch('email');

  return (
    <Form className={`flex flex-col items-center ${className || ''}`} onSubmit={handleSubmit(onSubmit)} {...rest}>
      <InputField
        className="mb-8 w-[280px] md:w-[300px]"
        control={control}
        name="email"
        placeholder={t(translations.INPUTS.EMAIL)}
        primaryColor={primaryColor}
        size="md"
        type="email"
        hideError
      />

      <Button
        className="w-[245px] md:w-[275px] ml-[5px]"
        color={primaryColor}
        isDisabled={formState?.isSubmitting || !formState?.isValid || !currentEmail}
        isLoading={formState?.isSubmitting}
        size="sm"
        type="submit">
        {t(translations.COMMON.SIGN_UP)}
      </Button>

      {isCincelHost() && onAuth0Login && (
        <AuthSocialLogin
          isDisabled={isAuth0Loading}
          onClick={onAuth0Login}
          separatorLabel={t('common.or', 'o')}
        />
      )}

      <AuthActionLink
        label={t(translations.AUTH_PAGES.SIGNUP_HAVE_ACCOUNT)}
        actionText={t(translations.AUTH_PAGES.SIGNUP_LOGIN_HERE)}
        to="/login"
      />
    </Form>
  );
};
